import React from 'react';
import { useInView } from 'react-intersection-observer';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { Github, ExternalLink } from 'lucide-react';
import './Projects.css';

const Projects = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 300, damping: 15 }
    }
  };

  const projects = [
    {
      title: "CinePeer",
      description: "Cinematic desktop streaming app built on Electron: browse trending, popular, and top-rated movies and TV shows via TMDB, then stream torrents instantly through WebTorrent — an in-app player with custom controls, keyboard shortcuts, and live download stats plays while the torrent downloads. Zustand state, IPC bridge between the React renderer and the Node torrent engine, local HTTP stream server, watchlist, filter/sort, and a download manager with real-time progress.",
      tech: ["Electron", "React 18", "Vite", "WebTorrent", "Zustand", "React Router", "TMDB API", "YTS API", "EZTV API"],
      github: "https://github.com/QaziAbsaar/CinePeer",
      image: "/images/projects/CinePeer/image.jpeg"
    },
    {
      title: "Health Monitor — Ward CRM",
      description: "Production-grade IoT health-data dashboard for ward-scale patient monitoring: 10 patients, 6 disease profiles, live vitals streaming, and deterministic Tier 1 threshold alerting. Ingests SenML (RFC 8428) packs, splits storage between Supabase Postgres (metadata, RLS) and InfluxDB (time-series), streams 50 Hz-capable uPlot charts, and exports 7/30-day PDF reports. 41 Vitest tests, strict TypeScript end to end.",
      tech: ["Next.js 16", "React 19", "TypeScript", "Tailwind CSS 4", "Supabase", "InfluxDB", "SenML (RFC 8428)", "uPlot", "Vitest"],
      github: "https://github.com/QaziAbsaar/Health-Monitor",
      images: [
        "/images/projects/health-monitor/ward-overview.png",
        "/images/projects/health-monitor/concept-illustration.png"
      ]
    },
    {
      title: "OmniSolve AI — Autonomous Outbound Sales System",
      description: "Multi-tenant B2B outbound platform that discovers companies from lead sources, enriches them (contact-email scraping, qualifying signals, MX verification), drafts AI-personalized emails with DeepSeek, sends them on a SendGrid drip schedule, and classifies replies into CRM stages. FastAPI + SQLAlchemy async backend, Celery + Redis scheduled jobs, Supabase Postgres, and a Next.js + shadcn/ui dashboard. Every external service is flag-gated behind deterministic mocks, so the whole pipeline runs end-to-end with zero API keys.",
      tech: ["Python", "FastAPI", "SQLAlchemy", "Celery", "Redis", "Supabase", "DeepSeek", "SendGrid", "Next.js", "TypeScript", "shadcn/ui"],
      github: "https://github.com/Omnisolveai/Sales-autonoumous-system",
      image: "/images/projects/Sales.png"
    },
    {
      title: "MediScan AI — Disease Predictor & Drug Recommender",
      description: "Classical ML web app that predicts medical conditions from 133 symptom inputs across 41 disease classes, with severity scoring. End-to-end pipeline trains and auto-selects the best model among Random Forest, XGBoost, SVM, and Decision Tree; the live OpenFDA API recommends real pharmaceutical drugs based on the primary prediction. Interactive Gradio frontend with probability confidence charts for top-5 predictions, disease descriptions, and precautions.",
      tech: ["Python", "Scikit-learn", "XGBoost", "Pandas", "NumPy", "Gradio", "OpenFDA API", "Matplotlib", "Seaborn"],
      github: "https://github.com/QaziAbsaar",
      image: "/images/projects/Medi-Scan/Medi-scan.jpeg"
    },
    {
      title: "MDCAT Prep Web App – AI-Powered Exam Preparation Platform",
      description: "AI MDCAT prep platform: verified materials, AI questions, personalized plans, tracking. RAG pipeline (LangChain, ChromaDB, Mixtral 8x7B w/ GPT-4/Gemini fallbacks). React (Vite, TS, Tailwind, Zustand); FastAPI (SQLite, JWT). Docker, GitHub CI/CD.",
      tech: ["React.js", "Vite", "TypeScript", "Tailwind CSS", "Zustand", "FastAPI", "SQLite", "ChromaDB", "LangChain", "Mixtral 8x7B", "OpenAI GPT-4", "Google Gemini Pro", "Docker", "GitHub Actions"],
      github: "https://github.com/QaziAbsaar",
      images: [
        "/images/projects/mdcat-prep/Analytics.png",
        "/images/projects/mdcat-prep/Chat_Feature.png",
        "/images/projects/mdcat-prep/Dashboard.png",
        "/images/projects/mdcat-prep/MCqs _selection.png",
        "/images/projects/mdcat-prep/Mcqs_Questions.png"
      ]
    },
    {
      title: "AI-Powered JetBot",
      description: "An autonomous JetBot on Jetson Nano for person tracking and conversational interaction. Uses YOLOv5 and DeepSORT for tracking, with ROS for navigation and Riva for speech AI. Optimized for low-latency edge AI with TensorRT.",
      tech: ["Jetson Nano", "Python", "YOLOv5", "DeepSORT", "TensorRT", "ROS", "Riva Speech AI"],
      github: "https://github.com/QaziAbsaar",
      image: "/images/projects/jetbot.png"
    },
    {
      title: "LMS MCP Server - PAF-IAST University Automation",
      description: "A powerful Model Context Protocol (MCP) server that automates PAF-IAST LMS interactions with AI assistants like Claude, Cursor, and VS Code Copilot. Features smart authentication, CAPTCHA solving with Google Gemini Vision API, and comprehensive academic data access including attendance, marks, and timetable management.",
      tech: ["Python", "MCP", "Google Gemini API", "Selenium", "FastAPI", "OCR", "AI Integration"],
      github: "https://github.com/QaziAbsaar/LMS_MCP",
      image: "/images/projects/lms-mcp.png"
    }
  ];

  return (
    <section id="projects" className="section section-alt">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <div className="section-head">
            <span className="section-path">projects</span>
            <h2>Things I've built</h2>
          </div>
          
          <motion.div
            className="projects-grid"
            variants={containerVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            {projects.map((project, index) => (
              <ProjectCard key={index} project={project} itemVariants={itemVariants} index={index} />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const ProjectCard = ({ project, itemVariants, index }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const cardRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start']
  });
  const isEven = index % 2 === 0;
  const driftX = useTransform(scrollYProgress, [0, 1], isEven ? [40, -40] : [-40, 40]);

  useEffect(() => {
    if (project.images?.length > 0) {
      const timer = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % project.images.length);
      }, 3000);
      return () => clearInterval(timer);
    }
  }, [project.images]);

  return (
    <motion.div
      ref={cardRef}
      className="project-card"
      variants={itemVariants}
      style={prefersReducedMotion ? undefined : { x: driftX }}
    >
      <div className="project-image">
        {project.images?.length > 0 ? (
          <div className="slideshow">
            <AnimatePresence>
              <motion.img
                key={currentImage}
                src={project.images[currentImage]}
                alt={`${project.title} screenshot ${currentImage + 1}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
            </AnimatePresence>
          </div>
        ) : (
          <img src={project.image} alt={project.title} />
        )}
        <div className="project-overlay">
          <div className="project-links">
            <a href={project.github} target="_blank" rel="noopener noreferrer" aria-label={`View ${project.title} source on GitHub`}>
              <Github size={20} />
            </a>
            {project.demo && project.demo !== '#' && (
              <a href={project.demo} target="_blank" rel="noopener noreferrer" aria-label={`View ${project.title} live demo`}>
                <ExternalLink size={20} />
              </a>
            )}
          </div>
        </div>
      </div>
      <div className="project-content">
        <div className="project-header">
          <h3>{project.title}</h3>
        </div>
        <p>{project.description}</p>
        <div className="project-tech">
          {project.tech.map((tech, techIndex) => (
            <span key={techIndex} className="tech-tag">{tech}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default Projects;
